import { useState, useEffect, useCallback } from 'react';
import { parseDuration, getDurationRange, findDurationFormat } from '../utils/durationUtils';

const useLoanCalculator = (appConfig) => {
  const { productApiUrl: productUrl, productId: productName } = appConfig;

  const [amountSelected, setAmountSelected] = useState(0);
  const [durationSelected, setDurationSelected] = useState(0);
  const [currency, setCurrency] = useState('€');
  const [monthlyRepayments, setMonthlyRepayments] = useState(null);
  const [selectDurationFormatted, setSelectDurationFormatted] = useState(null);
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${productUrl}offer/?offerId=${productName}`);

        if (!res.ok) {
          throw new Error(`API error: ${res.status}`);
        }

        const data = await res.json();

        if (!data?.amountRange?.length) {
          throw new Error('Invalid product data');
        }

        setProductData(data);
        setCurrency(data.currency || '€');

        setAmountSelected(data.defaultAmount);

        const defaultDurationObj = parseDuration(data.defaultDuration);
        setDurationSelected(
          defaultDurationObj.unit === "D"
            ? defaultDurationObj.value / 30
            : defaultDurationObj.value
        );

        setSelectDurationFormatted(data.defaultDuration);

        const repaymentRes = await fetch(`${productUrl}calc/monthly-repayment?offerId=${productName}`);

        if (!repaymentRes.ok) {
          throw new Error(`Repayment API error: ${repaymentRes.status}`);
        }

        const repaymentData = await repaymentRes.json();

        if (!repaymentData) {
          throw new Error('Failed to load repayment data');
        }

        setMonthlyRepayments(repaymentData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching API data:", error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchProductData();
  }, [productName, productUrl]);


  const calculateInstallment = useCallback(() => {
    try {
      if (!monthlyRepayments || !amountSelected || !selectDurationFormatted) return null;

      const data = monthlyRepayments[amountSelected];
      if (!data) return null;

      return {
        installmentAmount: data[selectDurationFormatted] ?? 0,
        principal: amountSelected
      };
    } catch (error) {
      console.error("Error calculating installment:", error);
      return null;
    }
  }, [amountSelected, monthlyRepayments, selectDurationFormatted]);


  const handleAmountChange = useCallback((value) => {
    setAmountSelected(value);
    if (productData) {
      const {
        durationRange,
        amountRangeCombinationMethod,
        hasAmountRangeCombination,
        amountRangeCombination,
        upperConstant,
        lowerConstant
      } = productData;

      const validDurations = getDurationRange({
        durationRange,
        method: amountRangeCombinationMethod,
        hasCombination: hasAmountRangeCombination,
        combination: amountRangeCombination,
        upperConstant,
        lowerConstant,
        amountSelected: value
      });

      const durations = validDurations.map(d => {
        const { value, unit } = parseDuration(d);
        return unit === "D" ? value / 30 : value;
      });

      if (!durations.includes(durationSelected)) {
        const newDuration = Math.max(...durations);
        setDurationSelected(newDuration);
        const newDurationFormatted = findDurationFormat(durationRange, newDuration);
        setSelectDurationFormatted(newDurationFormatted);
      }
    }
  }, [productData, durationSelected]);

  const handleDurationChange = useCallback((value) => {
    setDurationSelected(value);

    if (productData?.durationRange) {
      const newDurationFormatted = findDurationFormat(productData.durationRange, value);
      setSelectDurationFormatted(newDurationFormatted);
    }
  }, [productData]);

  const fetchSecciDocument = useCallback(async () => {
    try {
      const response = await fetch(
        `${productUrl}documents/secci?amount=${amountSelected}&duration=${durationSelected*30}&offer=${productName}`
      );

      if (!response.ok) {
        throw new Error(`SECCI API error: ${response.status}`);
      }

      const data = await response.text();
      return data;
    } catch (error) {
      console.error("Error fetching SECCI:", error);
      throw error;
    }
  }, [productUrl, amountSelected, durationSelected, productName]);


  const getValidDurations = useCallback(() => {
    if (!productData) return [];

    const {
      durationRange,
      amountRangeCombinationMethod,
      hasAmountRangeCombination,
      amountRangeCombination,
      upperConstant,
      lowerConstant
    } = productData;

    return getDurationRange({
      durationRange,
      method: amountRangeCombinationMethod,
      hasCombination: hasAmountRangeCombination,
      combination: amountRangeCombination,
      upperConstant,
      lowerConstant,
      amountSelected
    });
  }, [productData, amountSelected]);

  return {
    loading,
    error,
    productData,
    amountSelected,
    durationSelected,
    currency,
    calculateInstallment,
    handleAmountChange,
    handleDurationChange,
    fetchSecciDocument,
    getValidDurations,
  };
};

export default useLoanCalculator;
